// ============================================================
// MySQLFilteringSortingFunctions.jsx
//
// Filtering, Sorting & Functions
// Foundation · Module 04
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
} from "@mui/material";

/* Icons */
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SortIcon from "@mui/icons-material/Sort";
import FunctionsIcon from "@mui/icons-material/Functions";
import CodeIcon from "@mui/icons-material/Code";
import SecurityIcon from "@mui/icons-material/Security";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold: "#DAA520",
    goldBright: "#FFD700",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "rgba(0,0,0,0.45)",
    codeText: "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
};

/* ───────────────── SUB COMPONENTS ───────────────── */

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 18px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                <Typography sx={{ fontWeight: 700, color: BRAND.white }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.7 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map(l => l.cmd).join("\n");

    const copy = () => {
        navigator.clipboard.writeText(fullText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 3 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map(c => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy"}>
                    <Box onClick={copy} sx={{ cursor: "pointer", color: BRAND.gold }}>
                        {copied ? <DoneIcon /> : <ContentCopyIcon />}
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{ p: 2 }}>
                {lines.map((l, i) => (
                    <Box key={i} sx={{ mb: 1 }}>
                        {l.comment && (
                            <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: BRAND.muted }}>
                                -- {l.comment}
                            </Typography>
                        )}
                        <Typography sx={{ fontFamily: "monospace", color: BRAND.codeText }}>
                            {l.cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 2 }}>
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    p: 2,
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    background: BRAND.surface,
                }}
            >
                <Box sx={{ display: "flex", gap: 1 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold } })}
                    <Typography sx={{ color: BRAND.white }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>

            <Collapse in={open}>
                <Box sx={{ p: 2, background: "rgba(0,0,0,0.25)" }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

/* ───────────────── MAIN PAGE ───────────────── */

export default function MySQLFilteringSortingFunctions() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6 }}>
            <Container maxWidth="lg">

                {/* HERO */}
                <Chip
                    icon={<SchoolIcon sx={{ color: BRAND.gold }} />}
                    label="Foundation · Module 04"
                    sx={{ mb: 2, background: BRAND.goldGlow, color: BRAND.gold }}
                />

                <Typography variant="h3" sx={{ ...sectionHeading, mb: 2 }}>
                    Filtering, Sorting & <span style={{ color: BRAND.gold }}>Functions</span>
                </Typography>

                <Typography sx={{ color: BRAND.muted, maxWidth: 720 }}>
                    Raw data is useless without control. Filtering decides *what* rows you see,
                    sorting decides *in what order*, and SQL functions let you transform and
                    summarize data like a professional.
                </Typography>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* CONCEPT */}
                <SectionTitle icon={<LightbulbIcon />} label="Why Filtering & Functions Matter" />
                <Typography sx={{ color: BRAND.muted, mb: 3 }}>
                    In real systems, tables grow large quickly. You almost never want all rows.
                    SQL gives you precise tools to narrow results, order them meaningfully,
                    and compute values directly inside the database.
                </Typography>

                {/* KEY CONCEPTS */}
                <SectionTitle icon={<MenuBookIcon />} label="Core Concepts" />
                <Grid container spacing={2}>
                    {[
                        { icon: <FilterAltIcon />, title: "WHERE", description: "Filters rows based on conditions using operators like =, <, >, LIKE, IN." },
                        { icon: <SortIcon />, title: "ORDER BY", description: "Sorts result sets in ascending (ASC) or descending (DESC) order." },
                        { icon: <FunctionsIcon />, title: "Aggregate Functions", description: "COUNT, SUM, AVG, MIN, MAX summarize many rows into one value." },
                        { icon: <FunctionsIcon />, title: "Scalar Functions", description: "Operate on single values: UPPER, LOWER, LENGTH, ROUND, NOW()." },
                        { icon: <SecurityIcon />, title: "Filtering Safely", description: "Correct filtering prevents accidental data exposure and slow queries." },
                    ].map(c => (
                        <Grid item xs={12} sm={6} md={4} key={c.title}>
                            <ConceptCard {...c} />
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* LAB */}
                <SectionTitle icon={<CodeIcon />} label="Try It Yourself (Filtering & Sorting)" />

                <Typography sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>
                    🟡 WHERE – Filtering Rows
                </Typography>
                <CodeBlock
                    lines={[
                        { comment: "Exact match", cmd: "SELECT * FROM students WHERE id = 2;" },
                        { comment: "Text matching", cmd: "SELECT * FROM students WHERE email LIKE '%academy.com';" },
                        { comment: "Multiple conditions", cmd: "SELECT * FROM students WHERE id > 1 AND id < 5;" },
                        { comment: "List filtering", cmd: "SELECT * FROM students WHERE id IN (1, 3, 5);" },
                    ]}
                />

                <Typography sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>
                    🟡 ORDER BY – Sorting Results
                </Typography>
                <CodeBlock
                    lines={[
                        { comment: "Ascending order (default)", cmd: "SELECT * FROM students ORDER BY full_name;" },
                        { comment: "Descending order", cmd: "SELECT * FROM students ORDER BY created_at DESC;" },
                        { comment: "Sort + limit", cmd: "SELECT * FROM students ORDER BY id DESC LIMIT 3;" },
                    ]}
                />

                <Typography sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>
                    🟡 Aggregate Functions
                </Typography>
                <CodeBlock
                    lines={[
                        { comment: "Count rows", cmd: "SELECT COUNT(*) AS total_students FROM students;" },
                        { comment: "Minimum & maximum", cmd: "SELECT MIN(id), MAX(id) FROM students;" },
                        { comment: "Average", cmd: "SELECT AVG(id) FROM students;" },
                    ]}
                />

                <Typography sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>
                    🟡 Scalar Functions
                </Typography>
                <CodeBlock
                    lines={[
                        { comment: "Text functions", cmd: "SELECT UPPER(full_name), LENGTH(full_name) FROM students;" },
                        { comment: "Date & time", cmd: "SELECT NOW();" },
                        { comment: "Math functions", cmd: "SELECT ROUND(AVG(id), 2) FROM students;" },
                    ]}
                />

                {/* ADVANCED */}
                <Typography sx={{ color: BRAND.white, fontWeight: 700, mt: 4, mb: 2 }}>
                    🔽 Deeper Examples (click to expand)
                </Typography>

                <ExpandableExample title="Filtering with BETWEEN & NULL checks" icon={<FilterAltIcon />}>
                    <CodeBlock
                        lines={[
                            { comment: "Range filtering", cmd: "SELECT * FROM students WHERE id BETWEEN 2 AND 5;" },
                            { comment: "Check for missing values", cmd: "SELECT * FROM students WHERE email IS NULL;" },
                        ]}
                    />
                </ExpandableExample>

                <ExpandableExample title="GROUP BY with HAVING" icon={<FunctionsIcon />}>
                    <CodeBlock
                        lines={[
                            { comment: "Group and filter aggregated data", cmd: `
SELECT COUNT(*) AS total, created_at
FROM students
GROUP BY created_at
HAVING total > 1;
              ` },
                        ]}
                    />
                </ExpandableExample>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* LEARNED */}
                <SectionTitle icon={<StarIcon />} label="What You've Learned" />
                <Grid container spacing={2}>
                    {[
                        "How WHERE filters rows precisely",
                        "How ORDER BY controls result order",
                        "How to combine filters safely",
                        "How aggregate functions summarize data",
                        "How scalar functions transform values",
                        "How GROUP BY and HAVING work together",
                        "Why filtering affects performance",
                    ].map(item => (
                        <Grid item xs={12} sm={6} key={item}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <CheckCircleOutlineIcon sx={{ color: BRAND.gold }} />
                                <Typography sx={{ color: BRAND.muted }}>{item}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* FOOTER */}
                <Box sx={{ p: 3, background: BRAND.goldGlow, borderRadius: 2 }}>
                    <Typography sx={{ color: BRAND.white }}>
                        💡 <strong>Pro Tip:</strong> Always filter as early as possible.
                        Smaller result sets mean faster queries, safer updates, and happier users.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
